// Registro de uma visita de cobrança concluída.
//
// Grava em DOIS lugares, de propósito:
//
//   1. `rota_cobranca_paradas` — para a rota fazer sentido sozinha: o cobrador
//      abre o celular e vê o que já visitou e o que falta.
//   2. `cobranca_negociacao_historico`, como contato presencial — para a visita
//      entrar na timeline do cliente ao lado da ligação, do WhatsApp, da régua
//      automática e da notificação extrajudicial. É o que o gestor pediu:
//      "foi tentado tratar assim, entrar em contato, já foi negociado mas não
//      foi pago" precisa estar num lugar só, não espalhado por tela.
//
// O canal 'presencial' já existia no CHECK de `cnh_canal_check` desde a
// migration 20260825120000 — nada precisou mudar no banco para isso.

import { supabase } from './supabase';
import { ensureCobrancaCliente } from './cobrancaCliente';
import { registrarContatoCobranca } from '../services/api';
import { posicoesMandandoParaOFim } from './agrupamentoRota';
import type { CobrancaResultadoContato } from '../../types';

/** Desfechos possíveis de uma visita, no vocabulário já usado pela esteira. */
export const RESULTADOS_VISITA: Array<{ valor: CobrancaResultadoContato; rotulo: string }> = [
    { valor: 'atendeu', rotulo: 'Atendeu' },
    { valor: 'prometeu_pagar', rotulo: 'Prometeu pagar' },
    { valor: 'nao_atendeu', rotulo: 'Não atendeu / ninguém no local' },
    { valor: 'recusou_negociar', rotulo: 'Recusou negociar' },
    { valor: 'contato_invalido', rotulo: 'Endereço não confere' },
    { valor: 'outro', rotulo: 'Outro' },
];

export interface DadosVisitaCobranca {
    paradaId: string;
    cpfCnpjCliente?: string | null;
    clienteNome?: string | null;
    resultado: CobrancaResultadoContato;
    observacao: string;
    userId: string;
    userNome: string;
    /** Onde o cobrador estava ao registrar. Opcional: GPS falha e a visita vale igual. */
    coords?: { lat: number; lng: number } | null;
    /** 'pulado' para quem ele decidiu não visitar. */
    status?: 'visitado' | 'pulado';
    /**
     * Data (AAAA-MM-DD) em que a parada volta. Com ela a visita vale igual — vai
     * para a timeline —, mas a parada continua pendente nesta rota em vez de
     * sair dela. Ignora `status`.
     */
    reagendarPara?: string | null;
}

export interface ResultadoRegistro {
    /** A visita entrou na timeline do cliente? Falso quando não há CPF/CNPJ. */
    naTimeline: boolean;
    motivoForaDaTimeline?: string;
}

/**
 * Conclui uma parada da rota de cobrança.
 *
 * A observação é obrigatória: o resumo do que foi tratado é justamente o que o
 * gestor pediu, e visita concluída sem observação já foi problema no módulo
 * comercial (pedido do Bruno em 10/07) — o `VisitaModal` de lá exige pelo mesmo
 * motivo.
 *
 * A parada é gravada PRIMEIRO. Se a timeline falhar, o trabalho de campo não se
 * perde — e a função avisa quem chamou, em vez de fingir que deu tudo certo.
 */
export const registrarVisitaCobranca = async (
    dados: DadosVisitaCobranca,
): Promise<ResultadoRegistro> => {
    const observacao = String(dados.observacao || '').trim();
    if (!observacao) throw new Error('Descreva o que aconteceu na visita antes de concluir.');

    const agora = new Date().toISOString();
    const reagendarPara = dados.reagendarPara || null;

    const { error: erroParada } = await supabase
        .from('rota_cobranca_paradas')
        .update({
            // Reagendada continua pendente: é o que a mantém na rota. O que
            // aconteceu nesta ida fica em resultado/observação/visitado_em, e a
            // tela mostra como "última visita".
            status: reagendarPara ? 'pendente' : (dados.status || 'visitado'),
            reagendada_para: reagendarPara,
            resultado: dados.resultado,
            observacao,
            visitado_em: agora,
            checkin_lat: dados.coords?.lat ?? null,
            checkin_lng: dados.coords?.lng ?? null,
        })
        .eq('id', dados.paradaId);
    if (erroParada) throw erroParada;

    const cpf = String(dados.cpfCnpjCliente || '').replace(/\D/g, '');
    if (!cpf) {
        // A UC resolve o documento em 223 dos 230 casos antigos medidos, então
        // isso é raro — mas silêncio aqui viraria histórico perdido, e quem
        // registrou merece saber que a visita não entrou na ficha do cliente.
        return {
            naTimeline: false,
            motivoForaDaTimeline: 'Cliente sem CPF/CNPJ no cadastro: a visita ficou registrada na rota, mas não entrou no histórico do cliente.',
        };
    }

    const cobrancaCliente = await ensureCobrancaCliente(cpf, dados.clienteNome ?? null);
    if (!cobrancaCliente) {
        return {
            naTimeline: false,
            motivoForaDaTimeline: 'Não foi possível abrir a ficha de cobrança deste cliente. A visita ficou registrada na rota.',
        };
    }

    await registrarContatoCobranca({
        cobrancaClienteId: cobrancaCliente.id,
        userId: dados.userId,
        userNome: dados.userNome,
        canal: 'presencial',
        resultado: dados.resultado,
        // Na ficha do cliente a volta marcada aparece junto do que foi tratado.
        observacao: reagendarPara
            ? `${observacao}

Volta marcada para ${reagendarPara.split('-').reverse().join('/')}.`
            : observacao,
        contatoEm: agora,
        statusAtual: cobrancaCliente.status_negociacao,
    });

    return { naTimeline: true };
};

/**
 * Manda a parada para o fim da fila da rota — ver `posicoesMandandoParaOFim`.
 *
 * Lê as posições do banco, não da tela: outra pessoa pode ter reordenado a rota
 * depois que esta tela carregou. A removida fica de fora, como em toda a rota.
 */
export const mandarParadaParaOFim = async (rotaId: string, paradaId: string): Promise<void> => {
    const { data, error } = await supabase
        .from('rota_cobranca_paradas')
        .select('id, posicao')
        .eq('rota_id', rotaId)
        .neq('status', 'removida');
    if (error) throw error;
    const mudancas = posicoesMandandoParaOFim((data ?? []) as Array<{ id: string; posicao: number }>, paradaId);
    // Em lotes, como no reordenar: o PostgREST não tem UPDATE em lote com valores diferentes.
    for (let i = 0; i < mudancas.length; i += 20) {
        const erros = await Promise.all(mudancas.slice(i, i + 20).map(m =>
            supabase.from('rota_cobranca_paradas').update({ posicao: m.posicao })
                .eq('id', m.id).eq('rota_id', rotaId)
                .then(({ error: e }) => e)));
        const erro = erros.find(Boolean);
        if (erro) throw erro;
    }
};

/**
 * Reabre uma parada já visitada ou pulada, para ir de novo.
 *
 * O que foi registrado fica — resultado, observação e data —, e a tela mostra
 * como "última visita": quem volta à porta precisa saber o que já foi dito lá.
 * A timeline do cliente também não muda: a tentativa aconteceu de verdade.
 * A parada vai para o fim da fila e a rota encerrada volta a andar, senão a
 * parada reaberta sumiria junto com ela da lista de rotas em aberto.
 */
export const reabrirParadaCobranca = async (paradaId: string, rotaId: string): Promise<void> => {
    const { error } = await supabase
        .from('rota_cobranca_paradas')
        .update({ status: 'pendente', reagendada_para: null })
        .eq('id', paradaId);
    if (error) throw error;

    const { error: erroRota } = await supabase
        .from('rotas_cobranca')
        .update({ status: 'em_execucao', concluida_em: null })
        .eq('id', rotaId)
        .eq('status', 'concluida');
    if (erroRota) throw erroRota;

    await mandarParadaParaOFim(rotaId, paradaId);
};

/** Tira a data de volta: a parada reagendada entra de novo na lista de hoje. */
export const trazerReagendadaParaHoje = async (paradaId: string): Promise<void> => {
    const { error } = await supabase
        .from('rota_cobranca_paradas')
        .update({ reagendada_para: null })
        .eq('id', paradaId);
    if (error) throw error;
};

/** Marca a rota como concluída — some da lista de rotas em aberto no celular. */
export const concluirRotaCobranca = async (rotaId: string): Promise<void> => {
    const { error } = await supabase
        .from('rotas_cobranca')
        .update({ status: 'concluida', concluida_em: new Date().toISOString() })
        .eq('id', rotaId);
    if (error) throw error;
};

/** A rota passa a 'em_execucao' no primeiro registro do dia. */
export const iniciarRotaCobranca = async (rotaId: string): Promise<void> => {
    const { error } = await supabase
        .from('rotas_cobranca')
        .update({ status: 'em_execucao' })
        .eq('id', rotaId)
        .eq('status', 'planejada');
    if (error) throw error;
};
